// Modules
import React, { useEffect, useMemo } from 'react'
import { Redirect, useHistory } from 'react-router-dom'

// Application
import { useActivateHook, useRedirectExternalUniqueToken } from 'hooks'
import { HeaderProps, Contact } from 'typings'
import { ActivateStep } from 'typings/account'
import { usePageTracker } from 'hooks/usePageTracker'

// Components
import { Header, TemplateContainer } from 'components'
import { NewPasswordWrapper, ValidateTokenWrapper, SelectContactWrapper } from './Wrappers'
import { ConfirmContact } from 'components/Presenters/ConfirmContact'
import { pageviews as initialTrackedPageviews, steps } from './analytics'
import { ROUTES_PATH } from 'constants/routesPath'

const HeaderByStep: Record<ActivateStep, HeaderProps> = {
    [ActivateStep.newPassword]: {
        title: 'Crie uma senha',
        subtitle: 'Inclua os requisitos listados abaixo para criar uma senha segura:',
    },
    [ActivateStep.selectContact]: {
        title: 'Confirme que é você',
        subtitle: 'Escolha um desses contatos para receber o código de validação da sua conta Nav Dasa:',
    },
    [ActivateStep.validateToken]: {
        title: 'Insira o código',
        subtitle: 'Um código de segurança foi enviado para o [CONTACT_TYPE] <span>[CONTACT_VALUE]</span>.',
    },
}
export const ActivateAccount: React.VFC = () => {
    const { step, previousStep, hasMounted, contact, phoneContacts, emailContacts, cpf, isRecoverPassword } = useActivateHook()
    const history = useHistory()
    const { verifyQueryParamsAndSaveSessionStorage } = useRedirectExternalUniqueToken()
    const initialTrackedSteps = steps(!!isRecoverPassword, hasMounted)

    usePageTracker<ActivateStep>(initialTrackedSteps, step)
    usePageTracker<ActivateStep>(initialTrackedPageviews, step)

    const SUBTITLE_CONTENT: string = useMemo(() => {
        let {
            [step]: { subtitle },
        } = HeaderByStep
        if (step === ActivateStep.validateToken) {
            subtitle = subtitle.replace('[CONTACT_VALUE]', contact?.value || '')
            subtitle = subtitle.replace('[CONTACT_TYPE]', contact?.contactType === 'EMAIL' ? 'e-mail' : 'celular')
        }

        return subtitle
    }, [contact, step])

    useEffect(() => {
        verifyQueryParamsAndSaveSessionStorage()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const unblock = history.block((_, action) => {
            if (action === 'POP' && step !== ActivateStep.newPassword) {
                previousStep()
            }
            return
        })

        return () => {
            unblock()
        }
    }, [history, previousStep, step])

    if (!hasMounted) {
        return null
    }

    const contacts: Contact[] = []

    if (phoneContacts && phoneContacts.length > 0) {
        contacts.push(...phoneContacts)
    }

    if (emailContacts && emailContacts.length > 0) {
        contacts.push(...emailContacts)
    }

    if (step === ActivateStep.selectContact && contacts.length === 0) {
        return <ConfirmContact />
    }

    if (!cpf) {
        return <Redirect to={ROUTES_PATH.LOGIN} />
    }

    return (
        <TemplateContainer>
            <Header subtitle={SUBTITLE_CONTENT} title={HeaderByStep[step].title} />
            {step === ActivateStep.selectContact ? (
                <SelectContactWrapper contacts={contacts} />
            ) : step === ActivateStep.validateToken ? (
                <ValidateTokenWrapper />
            ) : (
                <NewPasswordWrapper />
            )}
        </TemplateContainer>
    )
}
